import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FAQJsonLd } from "@/components/seo/FAQJsonLd";

/**
 * Homepage FAQ section with LLM-optimized Q&A structure.
 * Uses semantic HTML and Schema.org FAQPage structured data.
 */

const homepageFAQs = [
  {
    question: "What is TrustedHeal?",
    answer:
      "TrustedHeal is an India-based medical tourism and healthcare facilitation platform that helps international patients access affordable, world-class medical treatment across top JCI/NABH-accredited hospitals in India. We are not a hospital—we are your dedicated healthcare concierge that manages your entire treatment journey from consultation to recovery.",
  },
  {
    question: "Which treatments does TrustedHeal support?",
    answer:
      "TrustedHeal supports a wide range of medical treatments including dental procedures (All-on-4, All-on-6 implants, veneers), cardiac surgery (bypass, valve replacement, angioplasty), orthopedic procedures (hip and knee replacement, spine surgery), oncology treatments, fertility services (IVF, IUI), organ transplants, ophthalmology, cosmetic surgery, and wellness programs.",
  },
  {
    question: "How much can I save with medical treatment in India?",
    answer:
      "Patients typically save 30-70% compared to US, UK, or European prices. For example, dental implants that cost $25,000-$50,000 in the US are available for $5,000-$10,000 in India with the same quality materials and JCI-accredited facilities. Cardiac bypass surgery costing $100,000+ in the US is available for $8,000-$15,000 in India.",
  },
  {
    question: "Are the hospitals in India safe and accredited?",
    answer:
      "Yes. TrustedHeal partners exclusively with JCI (Joint Commission International) and NABH (National Accreditation Board for Hospitals) accredited hospitals. These are the same international standards followed by top hospitals in the US and Europe. Our partner hospitals include Apollo, Fortis, Max, Medanta, and other leading healthcare institutions.",
  },
  {
    question: "What is included in TrustedHeal's service?",
    answer:
      "TrustedHeal provides end-to-end support including: free initial consultation and treatment planning, hospital and doctor matching based on your needs, transparent cost estimates with no hidden fees, visa assistance and travel coordination, airport pickup and local transportation, accommodation arrangements near the hospital, a dedicated care coordinator throughout your stay, and post-treatment follow-up support.",
  },
  {
    question: "How do I get started with TrustedHeal?",
    answer:
      "Getting started is simple: 1) Share your medical reports and requirements through our website, WhatsApp, or by speaking with our AI assistant. 2) Receive a personalized treatment plan with cost estimates within 24-48 hours. 3) Once approved, we handle all logistics including visa, travel, and hospital coordination. 4) Travel to India and receive world-class treatment with 24/7 support from your dedicated coordinator.",
  },
];

export function HomeFAQSection() {
  return (
    <>
      {/* Structured data for LLM/SEO */}
      <FAQJsonLd faqs={homepageFAQs} />

      <section className="py-16 lg:py-24 bg-background" aria-labelledby="faq-heading">
        <div className="container mx-auto px-4">
          {/* Section Header */}
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-block text-primary font-medium text-sm uppercase tracking-wider mb-4">
              Common Questions
            </span>
            <h2
              id="faq-heading"
              className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-secondary mb-4"
            >
              Frequently Asked Questions
            </h2>
            <p className="text-foreground/70 text-lg">
              Everything you need to know about medical tourism in India with TrustedHeal.
            </p>
          </div>

          {/* FAQ Accordion */}
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-3">
              {homepageFAQs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`faq-${index}`}
                  className="bg-card border border-border/50 rounded-xl px-6 shadow-soft data-[state=open]:shadow-hover transition-shadow"
                >
                  <AccordionTrigger className="text-left font-display font-semibold text-secondary hover:text-primary py-5 hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-foreground/80 pb-5 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Hidden semantic version for LLMs/Crawlers that don't process JS accordions well */}
          <div className="sr-only" aria-hidden="true">
            {homepageFAQs.map((faq, index) => (
              <div key={`static-faq-${index}`}>
                <h3>{faq.question}</h3>
                <p>{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
